import React, { FC } from 'react';
import { View, ScrollView, Text } from 'react-native';

import ColorWrapper from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import ModalHeader from '@components/modal/ModalHeader';
import JobInfoSection from './components/JobInfoSection';
import ProducedQuantitySection from './components/ProducedQuantitySection';
import TimeRegistrationSection from './components/TimeRegistrationSection';
import NoteSection from './components/NoteSection';
import RichTextEditorModal from './components/RichTextEditorModal';
import useManageFinishJobModal from './hooks/useManageFinishJobModal';
import useManageFinishJobNote from './hooks/useManageFinishJobNote';
import type { FinishJobModalProps } from './types';

const FinishJobModal: FC<FinishJobModalProps> = ({
  hide,
  item,
  machineId,
  originScreen,
  onFinishSuccess,
  C,
  scale = initialScale,
  font = initialFont,
}) => {
  const {
    editorMode,
    isRteModalOpen,
    rteKey,
    plainNoteContent,
    richNoteContent,
    handlePlainNoteChange,
    setRichNoteContent,
    setTouched: setNoteTouched,
    openRteModal,
    handleRteDone,
    handleRteCancel,
    noteLinks,
    imageDocumentReferences,
    onAttachItemPress,
    handleGenerateImageURL,
    hasNote,
    noteDetailsText,
    uploadNote,
    touched: noteTouched,
    clearDraft,
  } = useManageFinishJobNote({ item });

  const {
    producedQuantity,
    actualEndDate,
    isSubmitting,
    isValid,
    quantityTarget,
    quantityDelta,
    progressPercent,
    setupTimeRows,
    cycleTimeRows,
    provenanceText,
    isBatchDataLoading,
    incrementQuantity,
    decrementQuantity,
    setQuantityDirect,
    setActualEndDate,
    setActualSetupTime,
    setActualCycleTime,
    handleSubmit,
    handleCancel,
    submitError,
  } = useManageFinishJobModal({
    item,
    machineId,
    hide,
    hasNote,
    noteDetailsText,
    editorMode,
    uploadNote,
    noteTouched,
    clearDraft,
    originScreen,
    onFinishSuccess,
  });

  return (
    <View style={{ backgroundColor: C('Background/Light/Modal Highlight'), flex: 1, borderRadius: scale(SCREEN_BORDER_RADIUS), overflow: 'hidden' }}>
      <ModalHeader onCancel={handleCancel} onSubmit={handleSubmit} submitDisabled={!isValid || isSubmitting} submitText="Submit" title="Finish Job" scale={scale} font={font} C={C} />
      {submitError ? (
        <View style={{ backgroundColor: C('System/Light/Red'), paddingVertical: scale(8), paddingHorizontal: scale(24) }}>
          <Text style={[font['Footnote'], { color: C('Label/Light/AlwaysLight Primary') }]}>{submitError}</Text>
        </View>
      ) : null}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: scale(24) }}>
        <JobInfoSection item={item} actualEndDate={actualEndDate} onActualEndDateChange={setActualEndDate} scale={scale} font={font} C={C} />
        <ProducedQuantitySection
          producedQuantity={producedQuantity}
          quantityTarget={quantityTarget}
          quantityDelta={quantityDelta}
          progressPercent={progressPercent}
          onIncrement={incrementQuantity}
          onDecrement={decrementQuantity}
          onSetDirect={setQuantityDirect}
          scale={scale}
          font={font}
          C={C}
        />
        <TimeRegistrationSection
          setupTimeRows={setupTimeRows}
          cycleTimeRows={cycleTimeRows}
          producedQuantity={producedQuantity}
          onSetupTimeChange={setActualSetupTime}
          onCycleTimeChange={setActualCycleTime}
          provenanceText={provenanceText}
          isBatchDataLoading={isBatchDataLoading}
          scale={scale}
          font={font}
          C={C}
        />
        <NoteSection
          editorMode={editorMode}
          isRteModalOpen={isRteModalOpen}
          plainNoteContent={plainNoteContent}
          richNoteContent={richNoteContent}
          handlePlainNoteChange={handlePlainNoteChange}
          setRichNoteContent={setRichNoteContent}
          setTouched={setNoteTouched}
          openRteModal={openRteModal}
          handleRteDone={handleRteDone}
          handleRteCancel={handleRteCancel}
          noteLinks={noteLinks}
          imageDocumentReferences={imageDocumentReferences}
          onAttachItemPress={onAttachItemPress}
          handleGenerateImageURL={handleGenerateImageURL}
          scale={scale}
          font={font}
          C={C}
        />
      </ScrollView>

      {isRteModalOpen && (
        <RichTextEditorModal
          isVisible={isRteModalOpen}
          rteKey={rteKey}
          onCancel={handleRteCancel}
          onDone={handleRteDone}
          richNoteContent={richNoteContent}
          setRichNoteContent={setRichNoteContent}
          setTouched={setNoteTouched}
          noteLinks={noteLinks}
          imageDocumentReferences={imageDocumentReferences}
          onAttachItemPress={onAttachItemPress}
          handleGenerateImageURL={handleGenerateImageURL}
          scale={scale}
          font={font}
          C={C}
        />
      )}
    </View>
  );
};

export default ColorWrapper(FinishJobModal);
